import { apiClient } from '../utils/api-client';
import type {
  MembershipCard,
  MembershipCardPayload,
  MembershipCardResponse,
  MembershipCardsListResponse,
} from '../types/membership';

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  throw new Error(body.errors?.[0]?.detail || fallback);
}

class MembershipCardsService {
  /**
   * A member's cards. Wider than what this gym entered: a card from a global
   * body belongs to the climber, so the ones held on their other rosters come
   * back too, flagged `editable: false`.
   */
  async list(gymSlug: string, memberId: number): Promise<MembershipCard[]> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/members/${memberId}/membership_cards`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch membership cards');
    }

    const data: MembershipCardsListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async create(
    gymSlug: string,
    memberId: number,
    payload: MembershipCardPayload
  ): Promise<MembershipCard> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/members/${memberId}/membership_cards`,
      { data: { attributes: payload } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the card');
    }

    const data: MembershipCardResponse = await response.json();
    return data.data.attributes;
  }

  async update(
    gymSlug: string,
    memberId: number,
    id: number,
    payload: MembershipCardPayload
  ): Promise<MembershipCard> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/members/${memberId}/membership_cards/${id}`,
      { data: { attributes: payload } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the card');
    }

    const data: MembershipCardResponse = await response.json();
    return data.data.attributes;
  }

  async remove(gymSlug: string, memberId: number, id: number): Promise<void> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/members/${memberId}/membership_cards/${id}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the card');
    }
  }

  /**
   * Says whether a national card another gym entered counts as cover here.
   * Reversible: a gym can accept today and reject tomorrow.
   */
  async decide(
    gymSlug: string,
    memberId: number,
    id: number,
    verdict: 'accept' | 'reject'
  ): Promise<MembershipCard> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/members/${memberId}/membership_cards/${id}/${verdict}`,
      {}
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the card');
    }

    const data: MembershipCardResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Card types this gym has already typed, for the form's autocomplete. The
   * field is free text on purpose — every body names its own types.
   */
  async kinds(gymSlug: string, prefix: string): Promise<string[]> {
    const query = prefix ? `?q=${encodeURIComponent(prefix)}` : '';
    const response = await apiClient.get(`/api/gyms/${gymSlug}/membership_cards/kinds${query}`);

    if (!response.ok) return [];

    const data: { data?: string[] } = await response.json();
    return data.data ?? [];
  }

  /** The signed-in climber's own cards, for the settings page. Read-only. */
  async mine(): Promise<MembershipCard[]> {
    const response = await apiClient.get('/api/membership_cards');

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch your membership cards');
    }

    const data: MembershipCardsListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }
}

export const membershipCardsService = new MembershipCardsService();
